/**
 * ============================================================================
 * LAYOUT COMPONENT: Hope UI Styled Header / TopBar
 * ============================================================================
 * Purpose:
 *   Faithful implementation of the Hope UI navbar layout:
 *   - Clean search input with gray outline & Search... placeholder
 *   - "Go Pro" electric blue button with paper airplane icon (#3a57e8)
 *   - Circular US flag badge
 *   - Notification bell with unread indicator
 *   - Mail / messages envelope icon
 *   - User Profile with circular avatar, name & role ("Austin Robertson / Marketing Administrator")
 * ============================================================================
 */

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { getCurrentUser, getUserRole } from "../../core/utils/auth";
import {
  MagnifyingGlassIcon,
  BellIcon,
  EnvelopeIcon,
  SunIcon,
  MoonIcon,
  UserIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ShieldCheckIcon,
  WalletIcon,
  XMarkIcon,
  Bars3Icon,
} from "@heroicons/react/24/outline";

// Searchable platform modules for Quick Jump
const searchableModules = [
  { label: "Dashboard Overview", route: "/", category: "Overview" },
  { label: "Users Management", route: "/admin/users", category: "Master Admin" },
  { label: "Accounts & Credentials", route: "/admin/accounts", category: "Master Admin" },
  { label: "Roles & Permissions", route: "/admin/roles", category: "Master Admin" },
  { label: "Database Master", route: "/admin/databases", category: "Master Admin" },
  { label: "Telephony Carriers", route: "/admin/operators", category: "Master Admin" },
  { label: "Countries & Rates", route: "/admin/countries", category: "Master Admin" },
  { label: "Dynamic Menus", route: "/admin/menus", category: "Master Admin" },
  { label: "Login & Security Logs", route: "/admin/login-logs", category: "Master Admin" },
  { label: "Create Broadcast Campaign", route: "/workspace/campaigns/create", category: "Workspace" },
  { label: "Broadcast Campaign", route: "/workspace/campaigns", category: "Workspace" },
  { label: "General Campaign", route: "/workspace/campaigns", category: "Workspace" },
  { label: "General Messages", route: "/workspace/general-messages", category: "Workspace" },
  { label: "Report Management", route: "/workspace/reports", category: "Workspace" },
  { label: "Campaign Delivery Report", route: "/workspace/reports/campaigns", category: "Workspace" },
  { label: "General Messages Report", route: "/workspace/reports/general", category: "Workspace" },
  { label: "API Messages Report", route: "/workspace/reports/api", category: "Workspace" },
  { label: "Message Templates", route: "/workspace/templates", category: "Workspace" },
  { label: "Contacts & Groups", route: "/workspace/contacts", category: "Workspace" },
  { label: "WhatsApp Phone Numbers", route: "/admin/phone-numbers", category: "Master Admin" },
  { label: "Wallet & Ledgers", route: "/admin/ledgers", category: "Master Admin" },
  { label: "Message Delivery Logs", route: "/workspace/messages", category: "Workspace" },
  { label: "Support Desk & Tickets", route: "/workspace/tickets", category: "Workspace" },
];

const mockNotifications = [
  {
    _id: "1",
    title: "Meta Cloud API Connected",
    message: "Live Karix & Pinnacle telecom routes verified and healthy.",
    createdAt: new Date().toISOString(),
    isRead: false,
  },
  {
    _id: "2",
    title: "Recharge Completed",
    message: "₹24,500 credits balance topped up successfully.",
    createdAt: new Date(Date.now() - 3600000).toISOString(),
    isRead: true,
  },
];

const TopBar = ({
  onToggleSidebar,
  onToggleMobileSidebar,
  collapsed = false,
  onToggleCollapse = onToggleSidebar,
  mobileOpen = false,
  onClose = onToggleMobileSidebar,
}) => {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationMenuOpen, setNotificationMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);

  const searchRef = useRef(null);
  const userMenuRef = useRef(null);
  const notifMenuRef = useRef(null);

  const unreadCount = mockNotifications.filter((n) => !n.isRead).length;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
      if (notifMenuRef.current && !notifMenuRef.current.contains(e.target)) {
        setNotificationMenuOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const searchResults = searchQuery.trim()
    ? searchableModules.filter(
        (m) =>
          m.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
          m.category.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  const currentUser = getCurrentUser();
  const userRole = getUserRole(currentUser);

  const displayName = currentUser?.str_name || currentUser?.str_username || "SuperAdmin";
  const userInitials = useMemo(() => {
    if (currentUser?.str_name) {
      const parts = currentUser.str_name.trim().split(" ");
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return parts[0].substring(0, 2).toUpperCase();
    }
    if (currentUser?.str_username) {
      return currentUser.str_username.substring(0, 2).toUpperCase();
    }
    return "SA";
  }, [currentUser]);

  const roleTitle = useMemo(() => {
    if (userRole === "superadmin") return "SuperAdmin";
    if (userRole === "admin") return "Administrator";
    return "Client User";
  }, [userRole]);

  const handleSignOut = () => {
    setUserMenuOpen(false);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/signin");
  };

  return (
    <header className="bg-white dark:bg-[#151824] border-b border-[#e9ecef] dark:border-gray-800 sticky top-0 z-30 px-4 sm:px-6 py-3 transition-colors">
      <div className="flex items-center justify-between gap-4">
        {/* Left side: Mobile Toggle, Sidebar Collapse & Search Bar */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* Mobile hamburger */}
          <button
            onClick={onToggleMobileSidebar}
            className="lg:hidden p-1.5 text-[#8a92a6] hover:text-[#232d42] dark:hover:text-white cursor-pointer rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            title="Open navigation"
          >
            <Bars3Icon className="w-5 h-5" />
          </button>

          {/* Sidebar Collapse Toggle Icon (Just left of the Search Bar) */}
          <button
            onClick={onToggleCollapse}
            className="w-7 h-7 rounded-full bg-[#3a57e8] hover:bg-[#2e47d4] active:scale-95 text-white flex items-center justify-center shadow-xs cursor-pointer transition-all duration-150 flex-shrink-0"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2.5}
              stroke="currentColor"
            >
              {collapsed ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
              )}
            </svg>
          </button>

          {/* Hope UI Clean Search Input */}
          <div ref={searchRef} className="relative w-64 sm:w-80">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-[#8a92a6]">
              <MagnifyingGlassIcon className="w-4 h-4" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              placeholder="Search..."
              className="
                w-full pl-9 pr-8 py-1.5
                border border-[#e9ecef] dark:border-gray-700
                rounded-md bg-transparent
                text-sm text-[#232d42] dark:text-gray-100
                placeholder-[#8a92a6]
                focus:outline-none focus:border-[#3a57e8] dark:focus:border-[#3a57e8]
                transition-colors
              "
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-[#8a92a6] hover:text-[#232d42] cursor-pointer"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}

            {/* Quick Navigation Dropdown */}
            {searchFocused && searchResults.length > 0 && (
              <div className="absolute left-0 mt-2 w-full bg-white dark:bg-[#151824] rounded-lg shadow-xl border border-[#e9ecef] dark:border-gray-700 p-2 z-50 animate-fade-in max-h-72 overflow-y-auto">
                <div className="text-[10px] font-bold text-[#8a92a6] uppercase tracking-wider px-3 py-1">
                  Quick Navigation
                </div>
                <div className="space-y-0.5 mt-1">
                  {searchResults.map((item, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        navigate(item.route);
                        setSearchQuery("");
                        setSearchFocused(false);
                      }}
                      className="w-full flex items-center justify-between px-3 py-1.5 rounded-md text-left hover:bg-[#ebf0fe] dark:hover:bg-gray-800 transition-colors cursor-pointer"
                    >
                      <span className="text-xs font-medium text-[#232d42] dark:text-white">
                        {item.label}
                      </span>
                      <span className="text-[10px] text-[#8a92a6] font-mono">
                        {item.category}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right side: Hope UI Actions ("Go Pro", Flag, Bell, Mail, User) */}
        <div className="flex items-center space-x-3 sm:space-x-4">
          {/* Hope UI "Go Pro" Blue Pill Button */}
          

          {/* Appearance Toggle */}
          <button
            onClick={toggleTheme}
            className="p-1 text-[#8a92a6] hover:text-[#232d42] dark:hover:text-white transition-colors cursor-pointer"
            title="Toggle Appearance"
          >
            {isDark ? (
              <SunIcon className="w-5 h-5 text-amber-500" />
            ) : (
              <MoonIcon className="w-5 h-5 text-[#8a92a6]" />
            )}
          </button>

          {/* Hope UI Notification Bell */}
          <div ref={notifMenuRef} className="relative">
            <button
              onClick={() => {
                setUserMenuOpen(false);
                setNotificationMenuOpen(!notificationMenuOpen);
              }}
              className="p-1 text-[#8a92a6] hover:text-[#232d42] dark:hover:text-white transition-colors cursor-pointer relative"
              title="Notifications"
            >
              <BellIcon className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-rose-500 ring-2 ring-white dark:ring-gray-800"></span>
              )}
            </button>

            {/* Notification Dropdown */}
            {notificationMenuOpen && (
              <div className="absolute right-0 mt-3 w-80 bg-white dark:bg-[#151824] rounded-xl shadow-xl border border-[#e9ecef] dark:border-gray-700 p-3 z-50 animate-fade-in">
                <div className="px-3 py-2 flex justify-between items-center border-b border-[#e9ecef] dark:border-gray-800">
                  <span className="font-bold text-xs uppercase tracking-wider text-[#232d42] dark:text-white">
                    Notifications
                  </span>
                  {unreadCount > 0 && (
                    <span className="text-[10px] px-2 py-0.5 bg-[#3a57e8] text-white rounded-full font-bold">
                      {unreadCount} New
                    </span>
                  )}
                </div>

                <div className="max-h-64 overflow-y-auto divide-y divide-[#e9ecef] dark:divide-gray-800 py-1">
                  {mockNotifications.map((notif) => (
                    <div
                      key={notif._id}
                      className="p-2.5 rounded-lg hover:bg-[#f5f6fa] dark:hover:bg-gray-800/60 cursor-pointer transition-colors"
                    >
                      <div className="flex justify-between items-start mb-0.5">
                        <span className="font-semibold text-xs text-[#232d42] dark:text-white">
                          {notif.title}
                        </span>
                        <span className="text-[10px] text-[#8a92a6]">Just now</span>
                      </div>
                      <p className="text-xs text-[#8a92a6] line-clamp-2">{notif.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Hope UI Envelope Icon */}
          <button
            onClick={() => navigate("/workspace/messages")}
            className="p-1 text-[#8a92a6] hover:text-[#232d42] dark:hover:text-white transition-colors cursor-pointer hidden sm:block"
            title="Messages"
          >
            <EnvelopeIcon className="w-5 h-5" />
          </button>

          {/* Hope UI User Profile Capsule */}
          <div ref={userMenuRef} className="relative">
            <button
              onClick={() => {
                setNotificationMenuOpen(false);
                setUserMenuOpen(!userMenuOpen);
              }}
              className="flex items-center gap-2.5 cursor-pointer pl-1 pr-1.5 py-1 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              {/* Circular Avatar */}
              <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-[#3a57e8] to-[#00b4d8] text-white flex items-center justify-center font-extrabold text-sm shadow-xs flex-shrink-0">
                {userInitials}
              </div>

              {/* Stacked User Name & Role in Hope UI style */}
              <div className="text-left hidden md:block">
                <div className="text-sm font-bold text-[#232d42] dark:text-white leading-tight">
                  {displayName}
                </div>
                <div className="text-xs text-[#8a92a6] dark:text-gray-400 font-medium leading-tight">
                  {roleTitle} {currentUser?.str_account_id ? `• Acc #${currentUser.str_account_id}` : ""}
                </div>
              </div>
            </button>

            {/* Profile Menu Dropdown */}
            {userMenuOpen && (
              <div className="absolute right-0 mt-3 w-64 bg-white dark:bg-[#151824] rounded-xl shadow-xl border border-[#e9ecef] dark:border-gray-700 p-2 z-50 animate-fade-in">
                <div className="px-4 py-3 border-b border-[#e9ecef] dark:border-gray-800">
                  <div className="font-bold text-sm text-[#232d42] dark:text-white">
                    {displayName}
                  </div>
                  <div className="text-xs text-[#8a92a6]">
                    {currentUser?.str_email || `${currentUser?.str_username || "user"}@digiforest.io`}
                  </div>
                  <div className="mt-2 flex items-center justify-between px-2.5 py-1 rounded-md bg-[#ebf0fe] dark:bg-blue-950/40 text-[#3a57e8] dark:text-blue-300 text-xs font-semibold">
                    <span>Role</span>
                    <span className="font-semibold">{roleTitle}</span>
                  </div>
                </div>

                <div className="py-1 space-y-0.5">
                  <button
                    onClick={() => {
                      setUserMenuOpen(false);
                      navigate("/admin/users");
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium text-[#232d42] dark:text-gray-300 rounded-md hover:bg-[#ebf0fe] dark:hover:bg-gray-800 cursor-pointer transition-colors"
                  >
                    <UserIcon className="w-4 h-4 text-[#3a57e8]" />
                    <span>My Profile</span>
                  </button>

                  {userRole === "superadmin" && (
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        navigate("/admin/roles");
                      }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium text-[#232d42] dark:text-gray-300 rounded-md hover:bg-[#ebf0fe] dark:hover:bg-gray-800 cursor-pointer transition-colors"
                    >
                      <ShieldCheckIcon className="w-4 h-4 text-[#3a57e8]" />
                      <span>Role Permissions</span>
                    </button>
                  )}

                  <button
                    onClick={() => {
                      setUserMenuOpen(false);
                      navigate("/admin/ledgers");
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium text-[#232d42] dark:text-gray-300 rounded-md hover:bg-[#ebf0fe] dark:hover:bg-gray-800 cursor-pointer transition-colors"
                  >
                    <WalletIcon className="w-4 h-4 text-[#3a57e8]" />
                    <span>Wallet & Ledgers</span>
                  </button>
                </div>

                <div className="border-t border-[#e9ecef] dark:border-gray-800 pt-1">
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-rose-600 rounded-md hover:bg-rose-50 dark:hover:bg-rose-950/40 cursor-pointer transition-colors"
                  >
                    <ArrowRightOnRectangleIcon className="w-4 h-4" />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default TopBar;
